import api from './api';

const SUBSCRIBED_KEY = 'webPushSubscribed';

const isSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const base = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

/**
 * Register the SW + subscribe + send subscription to the server. Idempotent —
 * safe to call on every login. Returns true on success, false otherwise (denied,
 * unsupported, or transient error).
 */
export async function subscribePush() {
  if (!isSupported()) return false;

  try {
    let permission = Notification.permission;
    if (permission === 'default') permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await getRegistration();

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const { data } = await api.get('/notifications/vapid-public-key');
      const publicKey = data?.publicKey;
      if (!publicKey) {
        console.warn('[push] No VAPID public key configured on server');
        return false;
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await api.post('/notifications/push-tokens/web', { subscription });
    localStorage.setItem(SUBSCRIBED_KEY, '1');
    return true;
  } catch (err) {
    console.warn('[push] subscribePush failed:', err.message);
    return false;
  }
}

/**
 * Unsubscribe locally and tell the server to forget this subscription. Call on logout.
 */
export async function unsubscribePush() {
  if (!isSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    const subscription = registration && await registration.pushManager.getSubscription();
    if (subscription) {
      await api.delete('/notifications/push-tokens/web', { data: { endpoint: subscription.endpoint } }).catch(() => {});
      await subscription.unsubscribe().catch(() => {});
    }
    localStorage.removeItem(SUBSCRIBED_KEY);
  } catch {}
}

export function pushNotificationStatus() {
  if (!isSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}
